/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons/faExclamationTriangle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormattedMessage, FormattedRelativeTime, useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import { getSignInLink } from '../lib/authLinks'
import { Anchor, Box, Button, Modal, ModalProps, PasswordInput, Stack } from '@mantine/core'
import ButtonGroup from '../../../client/js/components/form/ButtonGroup'
import { useAuthContext } from './AuthProvider'
import { useDisclosure, useInterval } from '@mantine/hooks'
import { useCallback, useEffect } from 'react'
import { notifications } from '@mantine/notifications'
import { useNavigate } from 'react-router'
import { Form } from '@jalik/react-form'
import FormField from '../../../client/js/components/form/fields/FormField'
import FormErrors from '../../../client/js/components/form/FormErrors'
import useCustomForm from '../../../client/js/hooks/useCustomForm'

const NOTIF_ID = 'auth_expiration'

export type AuthExpiredModalProps = Omit<ModalProps, 'onClose' | 'opened'>

function AuthExpiredModal (props: AuthExpiredModalProps) {
  const auth = useAuthContext()
  const intl = useIntl()
  const location = useLocation()
  const navigate = useNavigate()
  const [opened, { close, open }] = useDisclosure(false)

  const signInPath = getSignInLink({
    redirect: location.pathname + location.search
  })

  const interval = useInterval(useCallback(() => {
    if (auth.context?.expiresAt != null) {
      const remainingTime = Math.round((auth.context.expiresAt - Date.now()) / 1000)

      if (auth.context?.authenticated && auth.context?.expiresAt <= Date.now()) {
        notifications.hide(NOTIF_ID)
        auth.signOut()
        navigate(signInPath)
      } else if (remainingTime <= 60 * 5) {
        notifications.show({
          id: NOTIF_ID,
          color: 'orange',
          icon: <FontAwesomeIcon icon={faExclamationTriangle} />,
          autoClose: false,
          message: <>
            <FormattedMessage
              id="session_expires_$"
              defaultMessage="Votre session expire {duration}."
              values={{
                duration: <FormattedRelativeTime
                  value={remainingTime}
                  updateIntervalInSeconds={1}
                />
              }}
            />
            <Box>
              <Anchor inherit onClick={open}>
                <FormattedMessage
                  id="click_here_to_sign_in_again"
                  defaultMessage="Cliquez ici pour vous reconnecter"
                />
              </Anchor>
            </Box>
          </>
        })
      }
    } else {
      notifications.hide(NOTIF_ID)
    }
  }, [auth, navigate, open, signInPath]), 1000)

  const form = useCustomForm({
    afterSubmit: 'clear',
    initialValues: {
      username: '',
      password: ''
    },
    onSubmit: ({ username, password }) =>
      auth.signIn(username, password),
    onSuccess () {
      close()
      notifications.hide(NOTIF_ID)
    }
  })

  useEffect(() => {
    if (auth.context?.authenticated) {
      interval.start()
    } else {
      interval.stop()
      notifications.hide(NOTIF_ID)
    }
    return () => {
      interval.stop()
    }
  }, [auth.context?.authenticated, interval])

  return (
    <Modal
      centered
      {...props}
      closeOnEscape={false}
      closeOnClickOutside={false}
      onClose={close}
      opened={opened}
      title={intl.formatMessage({
        id: 'authentication',
        defaultMessage: 'Authentification'
      })}
    >
      <Form context={form}>
        <Stack>
          <FormField
            name="username"
            label={intl.formatMessage({
              id: 'identifier',
              defaultMessage: 'Identifiant'
            })}
            required
          />
          <FormField
            name="password"
            label={intl.formatMessage({
              id: 'password',
              defaultMessage: 'Mot de passe'
            })}
            render={PasswordInput}
            required
          />
          <FormErrors />
        </Stack>
        <ButtonGroup mt="md">
          {/* @ts-ignore */}
          <Button
            {...form.getButtonProps()}
            onClick={close}
          >
            <FormattedMessage
              id="close"
              defaultMessage="Fermer"
            />
          </Button>
          {/* @ts-ignore */}
          <Button
            {...form.getButtonProps({ type: 'submit' })}
            loading={form.submitting}
            variant="filled"
          >
            <FormattedMessage
              id="sign_in"
              defaultMessage="Se connecter"
            />
          </Button>
        </ButtonGroup>
      </Form>
    </Modal>
  )
}

export default AuthExpiredModal
